export type CameraVector = [number, number, number];

export interface CameraAuthoringSnapshot {
  position: CameraVector;
  /** Euler degrees [pitch, yaw, roll]. */
  rotation: CameraVector;
  /** Normalized world-space direction through the centre of the view. */
  forward: CameraVector;
  fov: number;
}

export interface CameraAuthoringBundle {
  camera: CameraAuthoringSnapshot;
  cameraStart: {
    cameraStart: CameraVector;
    cameraYaw: number;
  };
  flythroughKeyframe: {
    position: CameraVector;
    target: CameraVector;
    fov: number;
  };
  panelPlacement: {
    position: CameraVector;
    rotation: CameraVector;
  };
}

const round = (value: number): number => Number(value.toFixed(3));
const roundVector = (value: CameraVector): CameraVector => value.map(round) as CameraVector;

function pointAhead(snapshot: CameraAuthoringSnapshot, distance: number): CameraVector {
  return [
    snapshot.position[0] + snapshot.forward[0] * distance,
    snapshot.position[1] + snapshot.forward[1] * distance,
    snapshot.position[2] + snapshot.forward[2] * distance,
  ];
}

/**
 * Build paste-ready world.json fragments from the current camera.
 *
 * The flythrough target is five metres through the crosshair. The panel seed is two metres
 * through the crosshair and kept upright while facing back toward the authoring camera.
 */
export function createCameraAuthoringBundle(
  snapshot: CameraAuthoringSnapshot,
  options: { targetDistance?: number; panelDistance?: number } = {},
): CameraAuthoringBundle {
  const targetDistance = options.targetDistance ?? 5;
  const panelDistance = options.panelDistance ?? 2;
  const camera = {
    position: roundVector(snapshot.position),
    rotation: roundVector(snapshot.rotation),
    forward: roundVector(snapshot.forward),
    fov: round(snapshot.fov),
  };

  return {
    camera,
    cameraStart: {
      cameraStart: camera.position,
      cameraYaw: camera.rotation[1],
    },
    flythroughKeyframe: {
      position: camera.position,
      target: roundVector(pointAhead(snapshot, targetDistance)),
      fov: camera.fov,
    },
    panelPlacement: {
      position: roundVector(pointAhead(snapshot, panelDistance)),
      rotation: [0, camera.rotation[1], 0],
    },
  };
}

export function formatCameraAuthoringBundle(snapshot: CameraAuthoringSnapshot): string {
  return JSON.stringify(createCameraAuthoringBundle(snapshot), null, 2);
}
